import { Component, OnInit, OnDestroy, inject, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { AgremoService } from '../../core/api-services/agremo.service';
import { AgremoAnalysisService } from '../../core/data-services/agremo-analysis.service';
import { AgremoAnalysis } from '../../core/data-models/agremo-analysis.model';
import { AgremoAnalysisStatus } from '../../core/data-models/agremo-analysis-status.model';
import { DateFormatPipe } from '../../shared/pipes';

interface FilterState {
  status: string;
  dateFrom: string;
  dateTo: string;
}

/**
 * Analysis List Component
 *
 * Displays all analyses for the current organization.
 * Supports optional mapId query parameter to filter analyses by map.
 * Follows the spray records list pattern.
 *
 * Route: /analyses
 * Query Params: ?mapId={id} (optional)
 */
@Component({
  selector: 'app-analyses',
  standalone: true,
  imports: [CommonModule, FormsModule, DateFormatPipe],
  templateUrl: './analyses.component.html',
  styleUrls: ['./analyses.component.scss']
})
export class AnalysesComponent implements OnInit, OnDestroy {
  private agremoService = inject(AgremoService);
  private agremoAnalysisService = inject(AgremoAnalysisService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);

  // loadAnalyses is awaited in ngOnInit; if the user navigates away from
  // /analyses before a slow request resolves with a stale-tier 403, the
  // continuation must not hijack whatever page they're on by then (Copilot
  // catch on PR #120).
  private destroyed = false;

  // Signal-based state
  private filtersSignal = signal<FilterState>({
    status: '',
    dateFrom: '',
    dateTo: ''
  });

  // Agremo availability (null = checking, true/false = known)
  readonly isConfigured = this.agremoService.isConfigured;

  // Computed signals from service
  readonly isLoading = this.agremoAnalysisService.isLoading;
  readonly error = this.agremoAnalysisService.error;
  readonly proRequired = this.agremoAnalysisService.proRequired;

  readonly analyses = computed(() => this.agremoAnalysisService.analyses());

  readonly filteredAnalyses = computed(() => {
    const filters = this.filtersSignal();
    let results = this.analyses();

    if (filters.status) {
      results = results.filter(a => a.status === filters.status);
    }
    if (filters.dateFrom) {
      results = results.filter(a => (a.requestedAt ?? '') >= filters.dateFrom);
    }
    if (filters.dateTo) {
      results = results.filter(a => !!a.requestedAt && a.requestedAt <= filters.dateTo);
    }

    return results;
  });

  // Computed stats
  readonly completedCount = computed(() =>
    this.analyses().filter(a => a.status === 'completed').length
  );

  readonly processingCount = computed(() =>
    this.analyses().filter(a =>
      a.status === 'processing' || a.status === 'queued' || a.status === 'pending' || a.status === 'downloading'
    ).length
  );

  readonly failedCount = computed(() =>
    this.analyses().filter(a => a.status === 'failed').length
  );

  // UI state
  isFilterOpen = signal(false);
  activeFilterCount = signal(0);

  // Getter/setter for template compatibility
  get filters(): FilterState {
    return this.filtersSignal();
  }

  set filters(value: FilterState) {
    this.filtersSignal.set(value);
    this.updateActiveFilterCount();
  }

  async ngOnInit(): Promise<void> {
    const configured = await this.agremoService.checkStatus();
    if (configured !== true) return;

    // Read mapId from query params to filter analyses by a specific map
    const mapId = this.route.snapshot.queryParamMap.get('mapId') || undefined;
    await this.agremoAnalysisService.loadAnalyses({ mapId });
    if (this.destroyed) return;

    // The route guard already checked tier on navigation in, but that check
    // reads a cached signal — if the org's tier dropped to Core after the
    // guard ran but before this landed (see ADN-2286's TG12 lifecycle E2E),
    // the 403 surfaces here instead. Send the user where the guard would
    // have sent them had its signal been fresh, rather than showing a raw
    // HTTP-failure banner.
    if (this.agremoAnalysisService.proRequired()) {
      this.router.navigate(['/analyses/preview']);
    }
  }

  ngOnDestroy(): void {
    this.destroyed = true;
  }

  // Filter methods
  toggleFilter(): void {
    this.isFilterOpen.update(open => !open);
  }

  updateActiveFilterCount(): void {
    const filters = this.filtersSignal();
    let count = 0;
    if (filters.status) count++;
    if (filters.dateFrom || filters.dateTo) count++;
    this.activeFilterCount.set(count);
  }

  clearFilters(): void {
    this.filtersSignal.set({
      status: '',
      dateFrom: '',
      dateTo: ''
    });
    this.activeFilterCount.set(0);
  }

  // Navigation
  addAnalysis(): void {
    this.router.navigate(['/analyses/new']);
  }

  viewAnalysis(analysis: AgremoAnalysis): void {
    this.router.navigate(['/analyses', analysis.id, 'view']);
  }

  // Status helpers
  getStatusText(status: AgremoAnalysisStatus): string {
    return this.agremoAnalysisService.getStatusText(status);
  }

  getStatusBadgeClass(status: AgremoAnalysisStatus): string {
    return this.agremoAnalysisService.getStatusBadgeClass(status);
  }

  getStatusIcon(status: AgremoAnalysisStatus): string {
    return this.agremoAnalysisService.getStatusIcon(status);
  }

  isProcessing(status: AgremoAnalysisStatus): boolean {
    return status === 'processing' || status === 'queued' || status === 'pending' || status === 'downloading';
  }

  trackByAnalysisId(index: number, analysis: AgremoAnalysis): string {
    return analysis.id;
  }
}
